import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { Accordion, Box, TextV2 } from '@withjoy/joykit';
import { ChevronDown, ChevronUp } from '@withjoy/joykit/icons';
import { animationTransition } from '@shared/utils/animationTransition';
import type { PaletteColorTokens } from '@withjoy/joykit/theme';

// --- Pill trigger (used by FilterPill) ---

export const PillTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    isOpen: boolean;
    label: React.ReactNode;
    onClick?: () => void;
    color?: keyof PaletteColorTokens;
    backgroundColor?: (isOpen: boolean) => BoxProps['backgroundColor'];
    pillHeight?: number;
    /** The pill is a disclosure for the filter rail: state and target for screen readers. */
    ariaExpanded?: boolean;
    /** Overrides the accessible name when the visible label is not a stable one (the cycling Feature pill). */
    ariaLabel?: string;
    ariaControls?: string;
    /**
     * Pointer entered (true) or left (false) the pill, and the same for keyboard focus.
     * Reported separately: one shared boolean un-freezes a cycling label when the pointer
     * leaves a pill that still holds focus, and vice versa.
     */
    onHoverChange?: (isHovered: boolean) => void;
    onFocusChange?: (isFocused: boolean) => void;
  }
>(({ isOpen, label, onClick, backgroundColor, color, pillHeight = 40, ariaExpanded, ariaLabel, ariaControls, onHoverChange, onFocusChange }, ref) => {
  const ArrowIcon = isOpen ? ChevronUp : ChevronDown;
  return (
    <Box
      backgroundColor={backgroundColor?.(isOpen) ?? (isOpen ? 'mono3' : 'mono2')}
      color={color ?? 'mono14'}
      as="button"
      height={pillHeight}
      paddingX={5}
      borderRadius={24}
      transition={animationTransition('background-color')}
      onClick={onClick}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      aria-controls={ariaControls}
      onMouseEnter={onHoverChange && (() => onHoverChange(true))}
      onMouseLeave={onHoverChange && (() => onHoverChange(false))}
      onFocus={onFocusChange && (() => onFocusChange(true))}
      onBlur={onFocusChange && (() => onFocusChange(false))}
      ref={ref}
    >
      <Box
        as="span"
        display="inline-flex"
        borderRadius="inherit"
        alignItems="center"
        columnGap={3}
        typographyVariant="button2"
        whiteSpace="nowrap"
      >
        {label}
        <ArrowIcon size={16} />
      </Box>
    </Box>
  );
});

// --- Drawer accordion trigger (used by FiltersDrawer) ---

/**
 * A drawer section's heading. Collapsed, it names the section's selection under the facet name
 * ("Beige, Black, Blue") — the drawer's stand-in for the desktop pills, which name theirs — so
 * the applied filters read off the drawer without a chip row on the page.
 */
export const AccordionTrigger = ({
  label,
  isExpanded,
  selectedNames,
  onClick,
}: {
  label: string;
  isExpanded: boolean;
  selectedNames: ReadonlyArray<string>;
  onClick: () => void;
}) => {
  const selection = selectedNames.join(', ');
  // Shown even when it repeats the heading ("Free" under "Free"): it is the collapsed section's
  // only sign that the filter is on.
  const showSelection = !isExpanded && !!selection;

  return (
    <Accordion.Button
      onClick={onClick}
      justifyContent="space-between"
      columnGap={4}
      paddingLeft={6}
      paddingRight={6}
      paddingY={4}
      minHeight={56}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        minWidth={0}
      >
        <TextV2 typographyVariant="hed1">{label}</TextV2>
        {showSelection && (
          <TextV2
            typographyVariant="label2"
            color="mono12"
            maxWidth="100%"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            data-testid="filters-drawer-selection"
          >
            {selection}
          </TextV2>
        )}
      </Box>
      <Box
        display="flex"
        flex="none"
        size={30}
        justifyContent="center"
        alignItems="center"
      >
        {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </Box>
    </Accordion.Button>
  );
};
